import React, { useReducer, useMemo } from "react";
import Dayjs from "dayjs";
import { useParams } from "react-router-dom";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";

// Custom.
import PreviewLayout from "containers/Layout/PreviewLayout";
import { Form, FormTabProps } from "@vocollege/components";
import I18n from "@vocollege/app/dist/modules/Services/I18n";
import {
  GET_CERTIFICATE,
  UPDATE_CERTIFICATE,
  CREATE_CERTIFICATE,
  SEARCH_GROUP,
} from "api";
import { VoAuth, regexPatterns } from "@vocollege/app";
import { reducer } from "@vocollege/app/dist/modules/VoHelpers";
import { useStyles } from "ui/styles/form";
import CertificatePreview from "containers/Templates/Certificate/CertificatePreview";
import { SEARCH_USER } from "@vocollege/app/dist/modules/VoApi";
import { FormProps } from "@vocollege/components/dist/Form";
import { getUserString } from "@vocollege/app/dist/modules/VoHelpers";
import BatchSelection from "components/BatchSelection";
import BatchPersonList, {
  BatchPersonListItem,
} from "components/BatchPersonList";
import {
  getPageTitle,
  getGroupTitle,
  getGroupTitleValue,
} from "services/Utils";
import SaveConsentDialog from "components/SaveConsentDialog";
import { EnhancedTableStatusCell } from "@vocollege/components";

const initialFormState = {
  formLoading: true,
  formState: {
    isDirty: false,
    isValid: false,
  },
  mode: "single",
};

const CertificateEdit: React.FC = () => {
  const initialDate = Dayjs().format("YYYY-MM-DD");
  const urlParams = useParams<any>();
  const { id } = urlParams;
  const classes = useStyles();
  const navigate = useNavigate();
  const [formState, formDispatch] = useReducer(reducer, initialFormState);
  
  // Methods.

  const getInitialState = () => {
    const initialState: { [key: string]: any } = {
      id: 0,
      firstname: "",
      lastname: "",
      personal_number: "",
      date: initialDate,
      author: null,
      principal: "",
      educator: null,
      education: "vardOchOmsorg",
      credits: 0,
      done: 0,
      list: [],
    };
    if (id && id === "skapa") {
      initialState.author = {
        id: VoAuth.currentUser.id,
        name: VoAuth.currentUser.name,
        type: "user",
      };
    }
    return initialState;
  };

  const [state, dispatch] = useReducer(reducer, getInitialState());

  const availableEducationValues = useMemo(() => {
    if (!state?.educator?.educations) return [];
    let educations: Record<string, boolean> = {};
    
    state.educator.educations.forEach((e: any) => {
      e?.education?.forEach((key: string) => educations[key] = e.title);
    });;
    return Object.entries(educations).map(([key, value], i) => {
      let label = I18n.get.vocertCertificate.educations[key] || value;
      return {label: label, value: key};
    });
  }, [state]);

  const getAvailableEducationValues = () => {
    return availableEducationValues;
  };

  const isMultiple = () => {
    return formState.mode === "multiple";
  };

  const handleCancel = () => {
    navigate("/intyg");
  };

  const handleComplete = (obj: any) => {
    if (isMultiple()) {
      navigate("/intyg");
    } else {
      navigate(`/intyg/${obj[Object.keys(obj)[0]].id}`);
    }
  };

  const handleFormChange: FormProps["onFormChange"] = (formState) => {
    formDispatch({ key: "formState", values: formState });
  };

  const handleBatchPersonListChange = (items: BatchPersonListItem[]) => {
    dispatch({
      values: {
        firstname: items[0].firstname,
        lastname: items[0].lastname,
        personal_number: items[0].personal_number,
        list: items,
      },
    });
  };

  const getExtraActions = () => {
    return (
      isMultiple() && (
        <>
          <BatchPersonList
            className="vo-batch-person-list-button"
            onChange={handleBatchPersonListChange}
          />
          <div className={classes.grow}></div>
        </>
      )
    );
  };

  const getTabs = () => {
    const tabs: FormTabProps[] = [];
    tabs.push({
      key: "content",
      label: I18n.get.misc.content,
      fields: [
        {
          name: "list",
          label: "",
          type: "custom",
          grid: {
            xs: 12,
          },
          render: (data: any) => <></>,
          overrideValue: state.list,
        },
        {
          name: "date",
          label: I18n.get.misc.date,
          type: "date_field",
          required: true,
          grid: {
            xs: 12,
            md: 6,
          },
          onChange: (value: any) => dispatch({ key: "date", values: value }),
        },
        {
          name: "credits",
          label: I18n.get.diploma.labels.credits,
          type: "text",
          required: true,
          grid: {
            xs: 12,
            md: 6,
          },
          validation: {
            required: true,
          },
          onChange: (value: any) => dispatch({ key: "credits", values: value }),
          params: {
            inputProps: {
              type: "number",
            },
          },
        },
        {
          name: "personal_number",
          label: I18n.get.user.labels.personalNumber,
          type: !isMultiple() ? "text" : "hidden",
          grid: {
            xs: 12,
            lg: 4,
          },
          params: {
            helperText: I18n.get.form.messages.personalNumberWhenPossible,
          },
          validation: {
            validate: (value: any) => {
              if (!value || value === "") {
                return true;
              }
              return (
                !!value.match(regexPatterns.personalNumber) ||
                I18n.get.form.errors.personalNumberInvalid
              );
            },
          },
          onChange: (value: any) =>
            dispatch({ key: "personal_number", values: value }),
        },
        {
          name: "firstname",
          label: I18n.get.user.labels.firstname,
          type: !isMultiple() ? "text" : "hidden",
          required: true,
          grid: {
            xs: 12,
            md: 6,
            lg: 4,
          },
          validation: {
            required: !isMultiple(),
          },
          onChange: (value: any) =>
            dispatch({ key: "firstname", values: value }),
        },
        {
          name: "lastname",
          label: I18n.get.user.labels.lastname,
          type: !isMultiple() ? "text" : "hidden",
          required: true,
          grid: {
            xs: 12,
            md: 6,
            lg: 4,
          },
          validation: {
            required: !isMultiple(),
          },
          onChange: (value: any) =>
            dispatch({ key: "lastname", values: value }),
        },
        {
          name: "educator",
          label: I18n.get.group.categories.educator,
          type: "entity_field",
          required: true,
          validation: {
            required: I18n.get.form.errors.required,
          },
          grid: {
            xs: 12,
          },
          params: {
            renderFieldValue: getGroupTitleValue,
            dialog: {
              types: ["group"],
              renderTitleField: getGroupTitle,
              extraDetails: (item: any) =>
                item?.status === 0 && (
                  <EnhancedTableStatusCell data={item.status} />
                ),
              query: SEARCH_GROUP,
              category: "searchGroup",
              variables: {
                categories: ["educator"],
              },
            },
          },
          onChange: (value: any) =>
            dispatch({ key: "educator", values: value }),
        },
        {
          name: "education",
          label: I18n.get.diploma.labels.education,
          type: "select",
          grid: {
            xs: 12,
            md: 9,
          },
          params: {
            availableValues: getAvailableEducationValues,
            disabled: !availableEducationValues || availableEducationValues.length < 1,
          },
          onChange: (value: any) => dispatch({key: "education", values: value }),
        },
        {
          name: "principal",
          label: I18n.get.diploma.labels.principal,
          type: "text",
          required: true,
          validation: {
            required: true,
          },
          grid: {
            xs: 12,
          },
          onChange: (value: any) =>
            dispatch({ key: "principal", values: value }),
          params: {
            inputProps: {
              autoComplete: "on",
            },
          },
        },
        {
          name: "author",
          label: I18n.get.misc.author,
          type: "entity_field",
          grid: {
            xs: 12,
          },
          params: {
            dialog: {
              renderTitleField: (item: any) => getUserString(item),
              types: ["user"],
              query: SEARCH_USER,
              category: "searchUser",
            },
            fields: {
              user: {
                title: "name",
              },
            },
          },
        },
      ],
    });
    return tabs;
  };

  return (
    <>
      <PreviewLayout
        className={classes.root}
        leftColumn={
          <>
            <Alert severity="warning" className={classes.row}>
              {I18n.get.vocertMessages.correctPreview}
            </Alert>
            {!state.uid && !formState.formLoading && (
              <Alert severity="error" className={classes.row}>
                {I18n.get.diploma.messages.saveBeforePrint}
              </Alert>
            )}
            {!formState.formLoading && id && id === "skapa" && (
              <BatchSelection
                onChange={(tab) => formDispatch({ key: "mode", values: tab })}
              />
            )}
            <Form
              tabs={getTabs()}
              labels={{
                loading: I18n.get.form.labels.loadingContent,
                create: I18n.get.certificate.labels.createCertificate,
                created: isMultiple()
                  ? I18n.get.certificate.messages.certificatesGenerated
                  : I18n.get.certificate.messages.certificateSaved,

                updated: I18n.get.certificate.messages.certificateSaved,
                deleted: I18n.get.certificate.messages.certificateDeleted,
                fieldRequired: I18n.get.form.errors.required,
              }}
              operations={{
                category: "certCertificate",
                get: GET_CERTIFICATE,
                create: CREATE_CERTIFICATE,
                update: UPDATE_CERTIFICATE,
              }}
              // paths={{
              //   back: "/intyg",
              // }}
              initialState={getInitialState()}
              renderPageTitle={(state) =>
                !isMultiple()
                  ? getPageTitle(
                      state,
                      I18n.get.certificate.labels.createCertificate
                    )
                  : ""
              }
              urlParams={urlParams}
              createParam="skapa"
              classes={{
                toolbar: classes.toolbar,
                paper: classes.form,
              }}
              onFormChange={handleFormChange}
              onQueryLoading={(loading) =>
                formDispatch({ key: "formLoading", values: loading })
              }
              onDataChange={(data, fetchedData) => {
                dispatch({
                  values: {
                    ...data,
                    uid: fetchedData.certCertificate.uid,
                  },
                });
              }}
              // onComplete={handleComplete}
              onComplete={handleComplete}
              onCancel={handleCancel}
              loadQueryOnParamsChange
              toolbarProps={{
                options: {
                  saveButton: {
                    label: isMultiple()
                      ? I18n.get.vocertActions.generate
                      : null,
                  },
                },
                extraActions: getExtraActions(),
              }}
            />
          </>
        }
        rightColumn={
          !formState.formLoading ? (
            <CertificatePreview
              item={state}
              formState={formState.formState}
              id={id}
            />
          ) : (
            <></>
          )
        }
        rightColumnProps={{
          className: clsx(classes.previewWrapper),
        }}
      />
      {id === "skapa" && <SaveConsentDialog context="Certificate" />}
    </>
  );
};

export default CertificateEdit;
